/**
 * NotificationPremiumPackageItem
 *
 * Covers the two premium-package notification types behind one component
 * varying only by `kind`:
 *   - premiumPackage        — `notification_premium_package_item.dart:22-104`
 *     (title `Earned through Premium {packageName}`; coins+date subtitle).
 *   - premiumPackageExpired — `notification_premium_package_expired_item.dart:21-77`
 *     (title `Your subscription to Premium {packageName} has expired`; date-only
 *     subtitle).
 *
 * Leading: `premium.png` icon tinted with the coins color (no avatar / user tap).
 * No trailing thumbnail.
 */
import React from 'react';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { MathUtils } from '@/domain/utils/mathUtils';
import { NotificationPremiumPackageKind } from '@/domain/enums/inbox/notificationPremiumPackageKind';
import { NotificationRowLayout } from '../NotificationRowLayout';
import { NotificationLeadingIcon } from './shared/NotificationLeadingIcon';
import { NotificationCoinsDateLine } from './shared/NotificationCoinsDateLine';
import type { NotificationItemProps } from './notificationItemProps';

interface NotificationPremiumPackageItemProps extends NotificationItemProps {
  kind: NotificationPremiumPackageKind;
}

export const NotificationPremiumPackageItem: React.FC<
  NotificationPremiumPackageItemProps
> = ({ kind, notification, onItemTap }) => {
  const { l10n } = useLocale();
  const { formatDate } = useDateFormatters();
  const { theme } = useTheme();
  const n = l10n.inbox.notifications;

  const { info } = notification;
  const premiumInfo = info.premiumPackageInfo;
  const packageName = premiumInfo?.packageName ?? '';
  const date = formatDate(info.timestamp);

  const title = (
    kind === NotificationPremiumPackageKind.premiumPackageExpired
      ? n.premiumPackageExpired
      : n.premiumPackageEarned
  ).replace('{packageName}', packageName);

  const subtitle =
    kind === NotificationPremiumPackageKind.premiumPackageExpired ? (
      date
    ) : (
      <NotificationCoinsDateLine
        amount={MathUtils.removeTrailingZero(premiumInfo?.coins ?? 0)}
        date={date}
      />
    );

  return (
    <NotificationRowLayout
      leading={
        <NotificationLeadingIcon
          iconPath="/assets/images/premium.png"
          iconColor={theme.exclusive}
        />
      }
      title={title}
      subtitle={subtitle}
      onItemTap={onItemTap ? () => onItemTap(notification) : undefined}
    />
  );
};
